import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowUpRight, Sunrise, FileText,
  FileSpreadsheet, BookHeart, Sparkles,
} from "lucide-react";

/* ------ Editable list ------ */
const resources = [
  {
    title: "The Miracle Morning",
    subtitle: "30-Day Transformational Journey",
    platform: "PDF",
    note: "Hal Elrod's classic morning practice, distilled into a 30-day printable challenge — the routine Tammy keeps returning to.",
    link: "https://miraclemorning.com/wp-content/uploads/2025/11/final-30-day-transformational-journey-update.pdf",
    icon: Sunrise,
    gradient: "from-[#F59E0B] to-[#1E3A8A]",
  },
  {
    title: "The 15-Point Plan",
    subtitle: "for Health & Happiness",
    platform: "Web",
    note: "A framework from the Win-Make-Give network for the fifteen practices that quietly compound into a great life.",
    link: "https://winmakegive.com/15-point-plan/",
    icon: BookHeart,
    gradient: "from-[#0F1E33] to-[#5A6E4E]",
  },
  {
    title: "GPS Business Plan",
    subtitle: "Goals · Priorities · Strategies",
    platform: "Google Doc",
    note: "The annual business plan template — define where you're going and the steps to get there.",
    link: "https://docs.google.com/document/d/1Z0aabsKEUOcqJH1vtBFwynk0o9-KbM73/edit?usp=sharing&ouid=103916521800636976298&rtpof=true&sd=true",
    icon: FileText,
    gradient: "from-[#4285F4] to-[#1A73E8]",
  },
  {
    title: "Growth Plan",
    subtitle: "Track your year",
    platform: "Google Sheets",
    note: "A working spreadsheet to track quarterly goals, numbers, and the rituals that move them.",
    link: "https://docs.google.com/spreadsheets/d/1QFtY9WHuWNdc5Ck4en1lJjroC8YvH7RTcy7zznT38xw/edit?usp=sharing",
    icon: FileSpreadsheet,
    gradient: "from-[#0F9D58] to-[#34A853]",
  },
];

export default function MorningsAndRhythms() {
  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="mornings-rhythms-page">
      <Link
        to="/lifestyles"
        className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition"
        data-testid="mornings-back"
      >
        <ArrowLeft size={14} /> Back to Lifestyles
      </Link>

      <div className="grid md:grid-cols-12 gap-12 mt-8 mb-16 items-end">
        <div className="md:col-span-7">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
            <Sunrise size={14} /> Mornings & Rhythms
          </div>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]">
            How the first hour
            <br />
            <span className="italic text-primary">shapes the day.</span>
          </h1>
        </div>
        <p className="md:col-span-4 md:col-start-9 text-lg text-foreground/75 leading-relaxed">
          The frameworks, plans, and practices Tammy actually uses to build a
          steady life and a steady business — alongside each other.
        </p>
      </div>

      {/* Featured quote / context */}
      <div className="relative bg-card border border-border rounded-2xl p-8 md:p-10 mb-12 overflow-hidden" data-testid="mornings-intro">
        <Sparkles className="absolute top-6 right-6 text-primary/40" size={22} strokeWidth={1.4} />
        <p className="font-serif italic text-2xl md:text-3xl leading-snug text-foreground/85 max-w-3xl">
          "What you do in the first hour of your day is what your life
          gets built out of — slowly, then all at once."
        </p>
      </div>

      {/* Resource cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {resources.map((r, i) => (
          <a
            key={r.title}
            href={r.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`mornings-resource-${i}`}
            className="group bg-card border border-border rounded-2xl overflow-hidden card-lift flex"
          >
            <div className={`w-1/3 min-h-full bg-gradient-to-br ${r.gradient} flex items-center justify-center p-6 relative`}>
              <r.icon className="text-white/95" size={44} strokeWidth={1.4} />
              <span className="absolute top-3 left-3 bg-white/90 text-foreground text-[9px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full">
                {r.platform}
              </span>
            </div>
            <div className="p-7 flex flex-col flex-1">
              <h3 className="font-serif text-2xl leading-tight">{r.title}</h3>
              <p className="text-xs uppercase tracking-[0.18em] text-foreground/55 mt-1.5">
                {r.subtitle}
              </p>
              <p className="text-sm text-foreground/75 mt-3 leading-relaxed flex-1">
                {r.note}
              </p>
              <span className="inline-flex items-center gap-1.5 mt-5 text-sm text-primary font-medium">
                Open <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </div>
          </a>
        ))}
      </div>

      <p className="text-xs text-foreground/55 mt-12 max-w-xl">
        These resources are free to use and adapt. The Google docs are
        view-only by default — make your own copy before editing.
      </p>
    </div>
  );
}
